import { FlaskConical } from 'lucide-react'
import { useEffect, useState } from 'react'
import { config } from '../../config'
import { listMockAccounts } from '../../auth/mockAuth'

/**
 * The honest label on the auth bypass.
 *
 * Same rule as the console's `PrototypeBanner`: a sign-in screen that accepts
 * anything must say so. A visitor who types a real password into a form that
 * silently ignores it has been misled about what the page does, and somebody will
 * eventually demo this as working authentication.
 *
 * `onFill` is offered on the sign-in screen only. The fixture accounts are worth
 * surfacing because they are not interchangeable — one is a Research Lead with the
 * review queue, one lands on the second workspace, one is unverified and routes to
 * the confirm-email screen. Without the list those branches are reachable only by
 * reading the JSON.
 *
 * The accounts load in an effect rather than at module scope so the fixture stays
 * in an async chunk (see the bundle note in `mockAuth.ts`). Until it arrives the
 * notice renders its text without the buttons, which is the correct degradation:
 * the disclosure is the part that matters.
 */
export function PrototypeNotice({
  onFill,
}: {
  onFill?: (credentials: { email: string; password: string }) => void
}) {
  const [accounts, setAccounts] = useState<
    { email: string; password: string; fullName: string; hint: string }[]
  >([])

  /**
   * The dependency is a boolean, not `onFill` itself. Callers pass an inline arrow,
   * so depending on the function identity would re-run the effect on every render —
   * and since the effect sets state, that is an infinite loop rather than a mere
   * wasted fetch. Only *whether* a fill handler exists can change the outcome.
   */
  const wantsAccounts = Boolean(onFill)

  useEffect(() => {
    if (!config.prototypeMode || !wantsAccounts) return
    let cancelled = false
    void listMockAccounts().then((loaded) => {
      if (!cancelled) setAccounts(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [wantsAccounts])

  if (!config.prototypeMode) return null

  return (
    <div className="flex flex-col gap-2 rounded-md border border-state-warning-border bg-state-warning-surface px-3 py-2.5 text-caption text-state-warning">
      <p className="flex items-start gap-2">
        <FlaskConical aria-hidden className="mt-0.5 size-3.5 shrink-0" />
        <span>
          <strong>Prototype mode.</strong> Sign-in is mocked from{' '}
          <code className="font-mono">mock_data/auth-mock.json</code> — any email and any password
          are accepted, and no password is ever checked or sent anywhere.
        </span>
      </p>

      {accounts.length > 0 && onFill && (
        <ul className="flex flex-col gap-1 ps-5">
          {accounts.map((account) => (
            <li key={account.email}>
              <button
                type="button"
                onClick={() => onFill({ email: account.email, password: account.password })}
                className="rounded-sm text-start underline underline-offset-2 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
              >
                Use {account.fullName}
                <span className="text-text-secondary"> — {account.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
